import { useEffect, useState } from 'react';

export const MIN_PATIENT_UHID_SEARCH_LENGTH = 3;
export const MIN_PATIENT_BILL_SEARCH_LENGTH = 1;
export const MIN_PATIENT_LOOKUP_SEARCH_LENGTH = 2;
export const PATIENT_UHID_SEARCH_LIMIT = 50;
export const PATIENT_UHID_INPUT_PLACEHOLDER = 'Enter at least 3 UHID characters';

export const normalizePatientUhidSearch = (value: string): string => value.trim();
export const normalizePatientGlobalSearch = (value: string): string => {
  const normalized = normalizePatientUhidSearch(value);
  return normalized.startsWith('#') ? `#${normalized.slice(1).trim()}` : normalized;
};

export const canSearchPatientUhid = (value: string): boolean => (
  normalizePatientUhidSearch(value).length >= MIN_PATIENT_UHID_SEARCH_LENGTH
);

export const isPatientBillSearch = (value: string): boolean => normalizePatientGlobalSearch(value).startsWith('#');

export const canSearchPatientGlobal = (value: string): boolean => {
  const normalized = normalizePatientGlobalSearch(value);
  return normalized.startsWith('#')
    ? normalized.slice(1).length >= MIN_PATIENT_BILL_SEARCH_LENGTH
    : canSearchPatientUhid(normalized);
};

export const canSearchPatientLookup = (value: string): boolean => (
  normalizePatientUhidSearch(value).length >= MIN_PATIENT_LOOKUP_SEARCH_LENGTH
);

export const patientUhidLookupPlaceholder = (value: string, isFetching: boolean): string => {
  if (!canSearchPatientUhid(value)) return 'Type 3+ UHID characters first';
  return isFetching ? 'Searching...' : 'Select patient / New patient';
};

export const usePatientLookupSearch = (value: string, delay = 300): string => {
  const normalized = normalizePatientUhidSearch(value);
  const [debounced, setDebounced] = useState(normalized);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(normalized), delay);
    return () => window.clearTimeout(timer);
  }, [delay, normalized]);

  return canSearchPatientLookup(normalized) && normalized === debounced ? debounced : '';
};

export const usePatientUhidSearch = (value: string, delay = 300): string => {
  const normalized = normalizePatientUhidSearch(value);
  const [debounced, setDebounced] = useState(normalized);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(normalized), delay);
    return () => window.clearTimeout(timer);
  }, [delay, normalized]);

  return canSearchPatientUhid(normalized) && normalized === debounced ? debounced : '';
};

export const usePatientGlobalSearch = (value: string, delay = 300): string => {
  const normalized = normalizePatientGlobalSearch(value);
  const [debounced, setDebounced] = useState(normalized);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(normalized), delay);
    return () => window.clearTimeout(timer);
  }, [delay, normalized]);

  return canSearchPatientGlobal(normalized) && normalized === debounced ? debounced : '';
};
